import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { stitchTheme } from './theme/theme';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuthStore } from './stores/authStore';
import type { UserType } from './stores/authStore';
import Layout from './components/Layout';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import DocumentBuilder from './pages/DocumentBuilder';
import DocumentLibrary from './pages/DocumentLibrary';
import DocumentViewer from './pages/DocumentViewer';
import AssignedDocument from './pages/AssignedDocument';
import AssignedDocuments from './pages/AssignedDocuments';
import LawyerManagement from './pages/LawyerManagement';
import { isDraftWorkflowRoute } from './utils/draftWorkflow';
import EstateLawyerAssignedDocuments from './pages/EstateLawyerAssignedDocuments';
import PrefilledDocuments from './pages/PrefilledDocuments';
import PrefilledDocumentDetail from './pages/PrefilledDocumentDetail';
import DocumentTemplates from './pages/DocumentTemplates';
import Alarm from './pages/Alarm';
import Assets from './pages/Assets';
import KeyVault from './pages/KeyVault';
import WelcomeQuestionnaire from './pages/WelcomeQuestionnaire';
import StuckFieldsResolver from './pages/StuckFieldsResolver';
import AdminWelcomeQuestionnaire from './pages/AdminWelcomeQuestionnaire';
import InvitationLinks from './pages/InvitationLinks';

import UserLists from './pages/UserLists';
import PersonalQuestionnaires from './pages/PersonalQuestionnaires';
import QuestionnaireManagement from './pages/QuestionnaireManagement';
import QuestionnaireEditor from './pages/QuestionnaireEditor';
import AssignedQuestionnaires from './pages/AssignedQuestionnaires';
import QuestionnaireAnswer from './pages/QuestionnaireAnswer';
import BetaFeedback from './pages/BetaFeedback';
import FeedbackManagement from './pages/FeedbackManagement';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedUserTypes?: UserType[] }> = ({ children, allowedUserTypes }) => {
  const { user, hasPendingQuestionnaires } = useAuthStore();
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}`;

  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  // Check if user is exempt from questionnaire requirements
  // Exempt: PLUS subscription (admin/lawyer), EL (Estate Lawyer) and EX (Estate Executor) user types
  const isExemptFromQuestionnaire =
    user.subscription === 'PLUS' ||
    user.userType === 'EL' ||
    user.userType === 'EX';

  // Keep draft creation and lawyer review available during questionnaire completion.
  if (hasPendingQuestionnaires && !isExemptFromQuestionnaire && !isDraftWorkflowRoute(location.pathname)) {
    return <Navigate to="/welcome-questionnaire" replace />;
  }

  return allowedUserTypes
    ? <RoleProtectedRoute allowedUserTypes={allowedUserTypes}>{children}</RoleProtectedRoute>
    : <>{children}</>;
};

// Welcome questionnaires are for Estate Owners. Lawyers only open this page to review
// a client's questionnaire (?ownerId=...), so send them to the dashboard otherwise
// (e.g. a stale /login?redirect=/welcome-questionnaire left behind by an EO session).
const WelcomeQuestionnaireRoute: React.FC = () => {
  const { user } = useAuthStore();
  const location = useLocation();
  if (user?.userType === 'EL' && !new URLSearchParams(location.search).get('ownerId')) {
    return <Navigate to="/" replace />;
  }
  return <WelcomeQuestionnaire />;
};

// TEMPORARY redirect helper — remove in cleanup phase once old routes are retired.
// Substitutes :param segments from the current route into the target path so
// redirected URLs (e.g. /form-builder-v2/:id) preserve their params.
const RedirectWithParams: React.FC<{ to: string }> = ({ to }) => {
  const params = useParams();
  const location = useLocation();
  let target = to;
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      target = target.replace(`:${key}`, value);
    }
  });
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
};

// TEMPORARY redirect helper — remove in cleanup phase once old routes are retired.
// Preserves the current query string (e.g. ?token=...) when redirecting to a new path.
const RedirectPreserveQuery: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
};

function App() {
  const { user } = useAuthStore();

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ThemeProvider theme={stitchTheme}>
          <CssBaseline />
          <Router>
            <Routes>
              <Route
                path="/login"
                element={user ? <Navigate to="/" replace /> : <Login />}
              />
              <Route
                path="/register"
                element={user ? <Navigate to="/" replace /> : <Register />}
              />
              <Route path="/assigned-documents/open" element={
                <ProtectedRoute allowedUserTypes={['EO', 'EL', 'EX']}>
                  <AssignedDocument />
                </ProtectedRoute>
              } />
              {/* Old wills-v2/assigned path — redirect to /assigned-documents/open (bookmarks preserved) */}
              <Route path="/wills-v2/assigned" element={
                <ProtectedRoute>
                  <RedirectPreserveQuery to="/assigned-documents/open" />
                </ProtectedRoute>
              } />
              <Route path="/welcome-questionnaire" element={
                <ProtectedRoute allowedUserTypes={['EO', 'EL']}>
                  <WelcomeQuestionnaireRoute />
                </ProtectedRoute>
              } />
              {/* Dashboard - All authenticated users */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              {/* Old forms path — no canonical Document equivalent; redirect to dashboard */}
              <Route path="/forms" element={
                <ProtectedRoute>
                  <Navigate to="/" replace />
                </ProtectedRoute>
              } />
              {/* Document Library - canonical */}
              <Route path="/document-library" element={
                <ProtectedRoute allowedUserTypes={['EO', 'EL', 'EX']}>
                  <Layout>
                    <DocumentLibrary />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/document-library/templates/:id" element={
                <ProtectedRoute allowedUserTypes={['EO', 'EL', 'EX']}>
                  <Layout>
                    <DocumentViewer />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/document-library/documents/:id" element={
                <ProtectedRoute allowedUserTypes={['EO', 'EL', 'EX']}>
                  <Layout>
                    <DocumentViewer />
                  </Layout>
                </ProtectedRoute>
              } />
              {/* Old wills-library paths — redirect to /document-library (bookmarks preserved) */}
              <Route path="/wills-library" element={
                <ProtectedRoute>
                  <Navigate to="/document-library" replace />
                </ProtectedRoute>
              } />
              <Route path="/wills-library/templates/:id" element={
                <ProtectedRoute>
                  <RedirectWithParams to="/document-library/templates/:id" />
                </ProtectedRoute>
              } />
              <Route path="/wills-library/submissions/:id" element={
                <ProtectedRoute>
                  <RedirectWithParams to="/document-library/documents/:id" />
                </ProtectedRoute>
              } />
              {/* Old wills path — redirect to /document-library (bookmarks preserved) */}
              <Route path="/wills" element={
                <ProtectedRoute>
                  <Navigate to="/document-library" replace />
                </ProtectedRoute>
              } />
              <Route path="/assigned-documents" element={
                <ProtectedRoute allowedUserTypes={['EO', 'EL', 'EX']}>
                  <Layout>
                    <AssignedDocuments />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/assigned-documents/:id" element={
                <ProtectedRoute allowedUserTypes={['EO', 'EL', 'EX']}>
                  <AssignedDocument />
                </ProtectedRoute>
              } />
              {/* Old wills-v2/assigned-list path — redirect to /assigned-documents (bookmarks preserved) */}
              <Route path="/wills-v2/assigned-list" element={
                <ProtectedRoute>
                  <Navigate to="/assigned-documents" replace />
                </ProtectedRoute>
              } />
              {/* EL-only: wills assigned by associated Estate Owners */}
              <Route path="/estate-lawyer/assigned-documents" element={
                <ProtectedRoute allowedUserTypes={['EL']}>
                  <Layout>
                    <EstateLawyerAssignedDocuments />
                  </Layout>
                </ProtectedRoute>
              } />
              {/* EL-only: blank PDFs pre-filled from client documents */}
              <Route path="/prefilled-documents" element={
                <ProtectedRoute allowedUserTypes={['EL']}>
                  <Layout>
                    <PrefilledDocuments />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/prefilled-documents/:id" element={
                <ProtectedRoute allowedUserTypes={['EL']}>
                  <Layout>
                    <PrefilledDocumentDetail />
                  </Layout>
                </ProtectedRoute>
              } />
              {/* EL-only: reusable document templates library */}
              <Route path="/estate-lawyer/document-templates" element={
                <ProtectedRoute allowedUserTypes={['EL']}>
                  <Layout>
                    <DocumentTemplates />
                  </Layout>
                </ProtectedRoute>
              } />
              {/* Old estate-lawyer/assigned-wills path — redirect to /estate-lawyer/assigned-documents (bookmarks preserved) */}
              <Route path="/estate-lawyer/assigned-wills" element={
                <ProtectedRoute allowedUserTypes={['EL']}>
                  <Navigate to="/estate-lawyer/assigned-documents" replace />
                </ProtectedRoute>
              } />
              <Route path="/assigned-documents/alarms" element={
                <RoleProtectedRoute allowedRoles={['PLUS']}>
                  <Layout>
                    <Alarm />
                  </Layout>
                </RoleProtectedRoute>
              } />
              {/* Old wills-v2/alarm path — redirect to /assigned-documents/alarms (bookmarks preserved) */}
              <Route path="/wills-v2/alarm" element={
                <RoleProtectedRoute allowedRoles={['PLUS']}>
                  <Navigate to="/assigned-documents/alarms" replace />
                </RoleProtectedRoute>
              } />
              <Route path="/documents/:id" element={
                <ProtectedRoute allowedUserTypes={['EO', 'EL', 'EX']}>
                  <Layout>
                    <DocumentViewer />
                  </Layout>
                </ProtectedRoute>
              } />
              {/* Old wills-v2/:id path — redirect to /documents/:id (bookmarks preserved) */}
              <Route path="/wills-v2/:id" element={
                <ProtectedRoute>
                  <RedirectWithParams to="/documents/:id" />
                </ProtectedRoute>
              } />

              <Route path="/lawyer-management" element={<ProtectedRoute allowedUserTypes={['EO']}><Layout><LawyerManagement /></Layout></ProtectedRoute>} />
              {/* Lawyer - Stuck Fields Resolver */}
              <Route path="/lawyer/stuck-fields/:userId" element={
                <RoleProtectedRoute allowedRoles={['PRO', 'PLUS']} allowedUserTypes={['EL']}>
                  <Layout>
                    <StuckFieldsResolver />
                  </Layout>
                </RoleProtectedRoute>
              } />

              {/* Admin - Questionnaire Config */}
              <Route path="/admin/welcome-questionnaire" element={
                <RoleProtectedRoute allowedRoles={['PLUS']}>
                  <Layout>
                    <AdminWelcomeQuestionnaire />
                  </Layout>
                </RoleProtectedRoute>
              } />
              <Route path="/admin/welcome-questionnaire/preview" element={
                <RoleProtectedRoute allowedRoles={['PLUS']}>
                  <WelcomeQuestionnaire previewMode />
                </RoleProtectedRoute>
              } />

              {/* Invitation Links - Lawyers and Estate Executors */}
              <Route path="/invitation-links" element={
                <ProtectedRoute>
                  <Layout>
                    <InvitationLinks />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Old form-builder path — redirect to /document-builder (bookmarks preserved) */}
              <Route path="/form-builder" element={
                <RoleProtectedRoute allowedRoles={['FREE', 'PRO', 'PLUS']} allowedUserTypes={['EO']}>
                  <Navigate to="/document-builder" replace />
                </RoleProtectedRoute>
              } />
              {/* Document Builder - canonical */}
              <Route path="/document-builder" element={
                <RoleProtectedRoute allowedRoles={['FREE', 'PRO', 'PLUS']} allowedUserTypes={['EO']}>
                  <Layout>
                    <DocumentBuilder />
                  </Layout>
                </RoleProtectedRoute>
              } />
              <Route path="/document-builder/:id" element={
                <RoleProtectedRoute allowedRoles={['FREE', 'PRO', 'PLUS']} allowedUserTypes={['EO']}>
                  <Layout>
                    <DocumentBuilder />
                  </Layout>
                </RoleProtectedRoute>
              } />
              {/* Old form-builder-v2 paths — redirect to /document-builder (bookmarks preserved) */}
              <Route path="/form-builder-v2" element={
                <RoleProtectedRoute allowedRoles={['FREE', 'PRO', 'PLUS']} allowedUserTypes={['EO']}>
                  <Navigate to="/document-builder" replace />
                </RoleProtectedRoute>
              } />
              <Route path="/form-builder-v2/:id" element={
                <RoleProtectedRoute allowedRoles={['FREE', 'PRO', 'PLUS']} allowedUserTypes={['EO']}>
                  <RedirectWithParams to="/document-builder/:id" />
                </RoleProtectedRoute>
              } />
              {/* Old form-builder/:id path — redirect to /document-builder/:id (bookmarks preserved) */}
              <Route path="/form-builder/:id" element={
                <RoleProtectedRoute allowedRoles={['FREE', 'PRO', 'PLUS']} allowedUserTypes={['EO']}>
                  <RedirectWithParams to="/document-builder/:id" />
                </RoleProtectedRoute>
              } />
              {/* Old form-management path — redirect to /document-library (bookmarks preserved) */}
              <Route path="/form-management" element={
                <RoleProtectedRoute allowedRoles={['FREE', 'PRO', 'PLUS']}>
                  <Navigate to="/document-library" replace />
                </RoleProtectedRoute>
              } />
              {/* Old form-reports path — no canonical Document equivalent; redirect to dashboard */}
              <Route path="/form-reports/:formId" element={
                <RoleProtectedRoute allowedRoles={['FREE', 'PRO', 'PLUS']}>
                  <RedirectPreserveQuery to="/" />
                </RoleProtectedRoute>
              } />
              {/* Assets - PRO/PLUS only */}
              <Route path="/assets" element={
                <RoleProtectedRoute allowedRoles={['PRO', 'PLUS']}>
                  <Layout>
                    <Assets />
                  </Layout>
                </RoleProtectedRoute>
              } />
              {/* Key Vault - PLUS only */}
              <Route path="/keyvault" element={
                <RoleProtectedRoute allowedRoles={['PLUS']}>
                  <Layout>
                    <KeyVault />
                  </Layout>
                </RoleProtectedRoute>
              } />

              {/* User Lists - All authenticated users */}
              <Route path="/user-lists" element={
                <ProtectedRoute>
                  <Layout>
                    <UserLists />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/personal-questionnaires" element={
                <ProtectedRoute>
                  <Layout>
                    <PersonalQuestionnaires />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Questionnaire Management - Lawyers only */}
              <Route path="/questionnaire-library" element={
                <RoleProtectedRoute allowedRoles={['PRO', 'PLUS']} allowedUserTypes={['EL']}>
                  <Layout>
                    <QuestionnaireManagement />
                  </Layout>
                </RoleProtectedRoute>
              } />
              <Route path="/questionnaire-library/create" element={
                <RoleProtectedRoute allowedRoles={['PRO', 'PLUS']} allowedUserTypes={['EL']}>
                  <Layout>
                    <QuestionnaireEditor />
                  </Layout>
                </RoleProtectedRoute>
              } />
              <Route path="/questionnaire-library/edit/:id" element={
                <RoleProtectedRoute allowedRoles={['PRO', 'PLUS']} allowedUserTypes={['EL']}>
                  <Layout>
                    <QuestionnaireEditor />
                  </Layout>
                </RoleProtectedRoute>
              } />
              <Route path="/questionnaire-library/preview/:id" element={
                <RoleProtectedRoute allowedRoles={['PRO', 'PLUS']} allowedUserTypes={['EL']}>
                  <WelcomeQuestionnaire previewMode />
                </RoleProtectedRoute>
              } />

              {/* Assigned Questionnaires - For users to view and complete */}
              <Route path="/assigned-questionnaires" element={
                <ProtectedRoute allowedUserTypes={['EO']}>
                  <Layout>
                    <AssignedQuestionnaires />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/assigned-questionnaires/:assignmentId" element={
                <ProtectedRoute allowedUserTypes={['EO']}>
                  <Layout>
                    <QuestionnaireAnswer />
                  </Layout>
                </ProtectedRoute>
              } />


              {/* Feedback - all authenticated users */}
              <Route path="/feedback" element={
                <ProtectedRoute>
                  <Layout>
                    <BetaFeedback />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Feedback Review - root/admin users only */}
              <Route path="/admin/feedback" element={
                <RoleProtectedRoute allowedRoles={['PLUS']}>
                  <Layout>
                    <FeedbackManagement />
                  </Layout>
                </RoleProtectedRoute>
              } />
            </Routes>
          </Router>
        </ThemeProvider>
      </GoogleOAuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
